import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '../../components/ui/pagination';
import { Home, Plus, Edit, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 12;

export default function AdminCovers() {
  const navigate = useNavigate();
  const [covers, setCovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCover, setEditingCover] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [formData, setFormData] = useState({
    issueNumber: '',
    title: '',
    tagline: '',
    coverImageUrl: '',
    publishedDate: '',
    featured: false
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchCovers(currentPage);
  }, [token, navigate, currentPage]);

  const fetchCovers = async (page) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/covers?page=${page}&pageSize=${PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both paginated and non-paginated response formats
      if (response.data && response.data.items) {
        setCovers(response.data.items || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalItems(response.data.total || 0);
      } else if (Array.isArray(response.data)) {
        setCovers(response.data);
        setTotalPages(1);
        setTotalItems(response.data.length);
      } else {
        setCovers([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching covers:', error);
      toast.error('Failed to load covers');
      setCovers([]);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        issueNumber: parseInt(formData.issueNumber),
        publishedDate: new Date(formData.publishedDate).toISOString()
      };

      if (editingCover) {
        await axios.put(`${API_URL}/api/covers/${editingCover.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cover updated successfully');
      } else {
        await axios.post(`${API_URL}/api/covers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cover created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchCovers(currentPage);
    } catch (error) {
      console.error('Error saving cover:', error);
      toast.error(error.response?.data?.detail || 'Failed to save cover');
    }
  };

  const handleEdit = (cover) => {
    setEditingCover(cover);
    const publishedDate = cover.publishedDate ? new Date(cover.publishedDate).toISOString().split('T')[0] : '';
    setFormData({
      issueNumber: cover.issueNumber.toString(),
      title: cover.title,
      tagline: cover.tagline || '',
      coverImageUrl: cover.coverImageUrl,
      publishedDate: publishedDate,
      featured: cover.featured
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this cover?')) return;

    try {
      await axios.delete(`${API_URL}/api/covers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cover deleted successfully');
      fetchCovers(currentPage);
    } catch (error) {
      console.error('Error deleting cover:', error);
      toast.error('Failed to delete cover');
    }
  };

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({...formData, coverImageUrl: response.data.url});
      toast.success('Cover image uploaded and compressed successfully!');
    } catch (error) {
      console.error('Error uploading cover image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const resetForm = () => {
    setEditingCover(null);
    setFormData({
      issueNumber: '',
      title: '',
      tagline: '',
      coverImageUrl: '',
      publishedDate: '',
      featured: false
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);
    
    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <>
      <Helmet>
        <title>Manage Covers · Admin</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur">
          <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <h1 className="font-display text-2xl font-bold">Covers</h1>
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/admin">
                    <Home className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-3xl font-bold mb-2">Manage Covers</h2>
              <p className="text-muted-foreground">
                {totalItems} total covers · Page {currentPage} of {totalPages}
              </p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="new-cover-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  New Cover
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingCover ? 'Edit Cover' : 'Create Cover'}</DialogTitle>
                  <DialogDescription>Fill in the cover details below</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="issueNumber">Issue Number</Label>
                      <Input
                        id="issueNumber"
                        type="number"
                        value={formData.issueNumber}
                        onChange={(e) => setFormData({...formData, issueNumber: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="publishedDate">Published Date</Label>
                      <Input
                        id="publishedDate"
                        type="date"
                        value={formData.publishedDate}
                        onChange={(e) => setFormData({...formData, publishedDate: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="tagline">Tagline (optional)</Label>
                    <Textarea
                      id="tagline"
                      value={formData.tagline}
                      onChange={(e) => setFormData({...formData, tagline: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="coverImageUrl">Cover Image</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input
                        id="coverImageUrl"
                        type="url"
                        value={formData.coverImageUrl}
                        onChange={(e) => setFormData({...formData, coverImageUrl: e.target.value})}
                        placeholder="https://... or upload below"
                        className="flex-1"
                        required
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploadingImage}
                        onClick={() => document.getElementById('cover-image-upload').click()}
                      >
                        {uploadingImage ? 'Uploading...' : 'Upload'}
                      </Button>
                      <input
                        id="cover-image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleCoverImageUpload}
                        className="hidden"
                      />
                    </div>
                    {formData.coverImageUrl && formData.coverImageUrl.startsWith('data:image') && (
                      <p className="text-xs text-muted-foreground mt-1">✓ Compressed image ready</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="featured"
                      checked={formData.featured}
                      onCheckedChange={(checked) => setFormData({...formData, featured: checked})}
                    />
                    <Label htmlFor="featured" className="cursor-pointer">
                      Featured on homepage
                    </Label>
                  </div>
                  <DialogFooter>
                    <Button type="submit">{editingCover ? 'Update' : 'Create'}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="covers-list">
                {covers.map((cover) => (
                  <Card key={cover.id} className="p-3 bg-[hsl(var(--surface-2))] border-border group">
                    <div className="relative aspect-[3/4] mb-3 overflow-hidden rounded">
                      <img 
                        src={cover.coverImageUrl} 
                        alt={`Issue ${cover.issueNumber}`}
                        className="w-full h-full object-cover"
                      />
                      {cover.featured && (
                        <div className="absolute top-2 right-2 bg-primary/90 backdrop-blur-sm rounded-full p-1.5">
                          <Star className="h-4 w-4 text-white fill-white" />
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <p className="text-xs uppercase tracking-wider text-primary font-ui mb-1">
                        Issue {cover.issueNumber}
                      </p>
                      <h3 className="font-display text-sm font-semibold line-clamp-1">
                        {cover.title}
                      </h3>
                      {cover.publishedDate && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(cover.publishedDate)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleEdit(cover)}
                        className="flex-1"
                        data-testid={`edit-cover-${cover.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleDelete(cover.id)}
                        data-testid={`delete-cover-${cover.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => handlePageChange(currentPage - 1)}
                          className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                      
                      {getPageNumbers()[0] > 1 && (
                        <>
                          <PaginationItem>
                            <PaginationLink onClick={() => handlePageChange(1)} className="cursor-pointer">
                              1
                            </PaginationLink>
                          </PaginationItem>
                          {getPageNumbers()[0] > 2 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                        </>
                      )}
                      
                      {getPageNumbers().map(page => (
                        <PaginationItem key={page}>
                          <PaginationLink 
                            onClick={() => handlePageChange(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      
                      {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
                        <>
                          {getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink onClick={() => handlePageChange(totalPages)} className="cursor-pointer">
                              {totalPages}
                            </PaginationLink>
                          </PaginationItem>
                        </>
                      )}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => handlePageChange(currentPage + 1)}
                          className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
